import { useCallback, useMemo, useState } from 'react';
import { Business, FactCheck } from '@mui/icons-material';
import { Chip, Typography } from '@mui/material';

import type { OrgOpenTaskCountsItem } from '../store/billingTasksApi';

import { BackButton } from '../components/back-button';
import { FilterableDataTable } from '../components/filterable-data-table';
import { useBillingTaskOrgsQuery } from '../store/hooks';
import { formatRelativeTime, getStalenessColor } from '../utils/formatting';

function ageDays(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Fleet-wide billing-task backlog (GDN-2644): every org with open or
 * in-progress tasks, worst first. Rows drill into the org's queue.
 */
export default function BillingTasks() {
  const { data, isLoading } = useBillingTaskOrgsQuery();
  const [search, setSearch] = useState('');

  const handleFiltersChange = useCallback((filters: Record<string, string>) => {
    setSearch(filters.search || '');
  }, []);

  // The endpoint returns the full fleet in one page; search filters client-side.
  const rows = useMemo(() => {
    const items = data?.items ?? [];
    if (!search) return items;
    const needle = search.toLowerCase();
    return items.filter(
      item =>
        (item.organization_name || '').toLowerCase().includes(needle) ||
        item.organization_id.toLowerCase().includes(needle),
    );
  }, [data, search]);

  const columns = useMemo(
    () => [
      {
        key: 'organization_name' as const,
        label: 'Organization',
        sortable: false,
        render: (value: unknown, row: OrgOpenTaskCountsItem) => (
          <div className="flex items-center">
            <Business className="h-4 w-4 text-gray-400 mr-2" />
            <span className="font-medium text-gray-900">
              {String(value || row.organization_id)}
            </span>
          </div>
        ),
      },
      {
        key: 'unfinished_count' as const,
        label: 'Unfinished',
        sortable: false,
        render: (value: unknown) => (
          <Chip
            label={String(Number(value || 0))}
            size="small"
            color="error"
            sx={{ fontWeight: 600, minWidth: 40 }}
          />
        ),
      },
      {
        key: 'open_count' as const,
        label: 'Open',
        sortable: false,
        render: (value: unknown) => (
          <span className="text-gray-900">{String(Number(value || 0))}</span>
        ),
      },
      {
        key: 'in_progress_count' as const,
        label: 'In progress',
        sortable: false,
        render: (value: unknown) => (
          <span className="text-gray-900">{String(Number(value || 0))}</span>
        ),
      },
      {
        key: 'oldest_unfinished_created_at' as const,
        label: 'Oldest task',
        sortable: false,
        render: (value: unknown) => {
          const iso = value ? String(value) : null;
          const days = ageDays(iso);
          return (
            <span className={getStalenessColor(days)}>{formatRelativeTime(iso)}</span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-8">
        <BackButton />

        <div className="flex items-center gap-3 mt-4 mb-6">
          <FactCheck className="text-blue-600" fontSize="large" />
          <div>
            <Typography variant="h4" className="font-bold text-gray-900">
              Billing Tasks
            </Typography>
            <Typography variant="body2" className="text-gray-500">
              Organizations with unfinished human billing work, worst backlog first
            </Typography>
          </div>
        </div>

        <FilterableDataTable<OrgOpenTaskCountsItem>
          title="Organizations"
          columns={columns}
          data={rows}
          total={rows.length}
          loading={isLoading}
          onFiltersChange={handleFiltersChange}
          getRowUrl={row => `/admin-v2/organization/${row.organization_id}?tab=billing-tasks`}
          emptyMessage="No organizations with unfinished billing tasks"
        />
      </main>
    </div>
  );
}
